import React, { forwardRef } from "react";
import { calcDose } from "../data/prescriptions";
import { PawPrint, Stethoscope, ClipboardList, GitCompareArrows } from "lucide-react";

/**
 * Print / PDF-optimised Rx pad.
 * Accepts editable overrides for medications, supportiveCare, diagnostics
 * and differentials — falls back to disease.* defaults when not provided.
 */
const RxPad = forwardRef(function RxPad(
  {
    disease,
    species,
    weight,
    vetName,
    patientName,
    date,
    medications,
    supportiveCare,
    diagnostics,
    differentials,
  },
  ref
) {
  const meds = medications ?? disease.medications ?? [];
  const care = supportiveCare ?? disease.supportiveCare ?? [];
  const dx = diagnostics ?? disease.diagnostics ?? [];
  const ddx = differentials ?? disease.differentials ?? [];
  const speciesLabel = species === "canine" ? "Canine (Dog)" : species === "feline" ? "Feline (Cat)" : "—";

  return (
    <div
      ref={ref}
      data-testid="rx-pad"
      className="rx-pad bg-white text-[#1A1F1D] w-full max-w-[820px] mx-auto p-10 border border-[#E2E8F0]"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      {/* Letterhead */}
      <div className="flex items-start justify-between border-b border-[#0F4C3A] pb-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-[#0F4C3A] flex items-center justify-center text-white">
            <PawPrint className="w-6 h-6" strokeWidth={2} />
          </div>
          <div>
            <div
              className="text-2xl leading-none tracking-tight"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 700 }}
            >
              Petsmate
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C6B66] mt-1.5">
              Veterinary Clinic & Prescription
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-[#5C6B66] leading-relaxed">
          <div className="uppercase tracking-[0.12em] font-semibold text-[#1A1F1D]">Rx / Prescription</div>
          <div className="mt-1">Date: <span className="text-[#1A1F1D]">{date}</span></div>
          <div>Ref: PM-{Date.now().toString().slice(-6)}</div>
        </div>
      </div>

      {/* Patient / Case block */}
      <div className="grid grid-cols-4 gap-6 mb-6 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6B66]">Patient</div>
          <div className="mt-1 font-medium" data-testid="rx-patient">{patientName || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6B66]">Species</div>
          <div className="mt-1 font-medium" data-testid="rx-species">{speciesLabel}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6B66]">Body Weight</div>
          <div className="mt-1 font-medium" data-testid="rx-weight">{weight ? `${weight} kg` : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6B66]">Attending Vet</div>
          <div className="mt-1 font-medium" data-testid="rx-vet">{vetName || "—"}</div>
        </div>
      </div>

      {/* Diagnosis */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6B66] mb-1">
          Working Diagnosis
        </div>
        <div
          className="text-lg tracking-tight"
          style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
          data-testid="rx-diagnosis"
        >
          {disease.name}
        </div>
        {disease.clinicalSigns && (
          <div className="text-sm text-[#5C6B66] mt-1 leading-relaxed">{disease.clinicalSigns}</div>
        )}
      </div>

      {/* Diagnostics */}
      {dx.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-[#0F4C3A]" />
            <div
              className="text-sm tracking-[0.12em] uppercase"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
            >
              Recommended Diagnostics
            </div>
          </div>
          <table className="w-full text-[13px] border-collapse" data-testid="rx-diagnostics">
            <thead>
              <tr className="border-y border-[#E2E8F0] text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6B66]">
                <th className="py-2 pr-3 font-semibold w-8">#</th>
                <th className="py-2 pr-3 font-semibold w-1/3">Test</th>
                <th className="py-2 font-semibold">Expected / Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {dx.map((t, i) => (
                <tr key={i} className="border-b border-[#E2E8F0] align-top">
                  <td className="py-2 pr-3 text-[#5C6B66]">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{t.test || "—"}</td>
                  <td className="py-2 text-[#1A1F1D]">{t.finding || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Differentials */}
      {ddx.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <GitCompareArrows className="w-4 h-4 text-[#0F4C3A]" />
            <div
              className="text-sm tracking-[0.12em] uppercase"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
            >
              Differentials to Rule Out
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="rx-differentials">
            {ddx.map((d, i) => (
              <span
                key={i}
                className="text-[12px] px-2.5 py-1 rounded-full border border-[#E2E8F0] bg-[#F9F8F6] text-[#1A1F1D]"
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Medications table */}
      {meds.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Stethoscope className="w-4 h-4 text-[#0F4C3A]" />
            <div
              className="text-sm tracking-[0.12em] uppercase"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
            >
              Treatment Plan
            </div>
          </div>
          <table className="w-full text-[13px] border-collapse" data-testid="rx-medications">
            <thead>
              <tr className="border-y border-[#E2E8F0] text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6B66]">
                <th className="py-2 pr-3 font-semibold w-8">#</th>
                <th className="py-2 pr-3 font-semibold">Drug</th>
                <th className="py-2 pr-3 font-semibold">Dose</th>
                <th className="py-2 pr-3 font-semibold">For {weight || 0} kg</th>
                <th className="py-2 pr-3 font-semibold">Route</th>
                <th className="py-2 pr-3 font-semibold">Freq.</th>
                <th className="py-2 font-semibold">Duration</th>
              </tr>
            </thead>
            <tbody>
              {meds.map((m, i) => {
                const totalDose = calcDose(m.doseMgPerKg, Number(weight));
                return (
                  <tr key={i} className="border-b border-[#E2E8F0] align-top">
                    <td className="py-3 pr-3 text-[#5C6B66]">{i + 1}</td>
                    <td className="py-3 pr-3">
                      <div className="font-medium">{m.drug}</div>
                      {m.notes && <div className="text-[11px] text-[#5C6B66] mt-0.5">{m.notes}</div>}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">{m.doseText}</td>
                    <td className="py-3 pr-3 whitespace-nowrap font-medium text-[#0F4C3A]">
                      {totalDose || <span className="text-[#5C6B66]">—</span>}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">{m.route}</td>
                    <td className="py-3 pr-3 whitespace-nowrap">{m.frequency}</td>
                    <td className="py-3 whitespace-nowrap">{m.duration}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Care instructions */}
      {care.length > 0 && (
        <div className="mb-6">
          <div
            className="text-sm tracking-[0.12em] uppercase mb-2"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
          >
            Care & Follow-up Instructions
          </div>
          <ul className="text-[13px] leading-relaxed space-y-1 list-disc pl-5 text-[#1A1F1D]">
            {care.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Signature */}
      <div className="mt-10 grid grid-cols-2 gap-8 text-xs text-[#5C6B66]">
        <div>
          <div className="border-t border-[#1A1F1D] pt-1">Veterinarian Signature</div>
          <div className="mt-1">{vetName || "—"}</div>
        </div>
        <div>
          <div className="border-t border-[#1A1F1D] pt-1">Clinic Stamp</div>
          <div className="mt-1">Petsmate Veterinary Clinic</div>
        </div>
      </div>

      <div className="mt-8 text-[10px] text-[#5C6B66] leading-relaxed border-t border-[#E2E8F0] pt-3">
        <strong className="text-[#1A1F1D]">Disclaimer:</strong> Doses generated by Petsmate Rx Assistant are based on
        published veterinary formulary ranges. The prescribing veterinarian is responsible for verifying
        contraindications, drug interactions and the patient&apos;s clinical status before dispensing.
      </div>
    </div>
  );
});

export default RxPad;
